import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useConnections, useSettings, useToolCounts } from '../api/hooks.js';
import type { Connection, ToolCounts } from '../api/types.js';
import { Badge, Card, ConfirmDialog, SectionLabel } from '../components/ui.js';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';

type ConnectorRow = Connection | {
  name: string;
  url?: string;
  authType?: string;
  connected?: boolean;
  configured?: boolean;
  userConnected?: boolean;
  userApiKeySet?: boolean;
  shared?: boolean;
  userEnabled?: boolean;
  supportsUserApiKey?: boolean;
  tools?: number;
};

export function Settings() {
  const settings = useSettings();
  const connections = useConnections();
  const toolCounts = useToolCounts();
  const d = settings.data;
  if (!d) return <p className="text-sm text-[var(--color-ink-muted)]">Loading…</p>;

  const list: ConnectorRow[] = connections.data?.length ? connections.data : d.connectors;

  return (
    <div>
      <div className="mb-6 pt-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-lg font-semibold">Settings</h1>
          <Badge tone={d.isAdmin ? 'good' : 'warn'}>{d.isAdmin ? 'Admin' : 'Member'}</Badge>
        </div>
        <p className="mt-1 text-sm text-[var(--color-ink-muted)]">Read-only view · secrets are masked</p>
      </div>

      <SectionLabel>Server</SectionLabel>
      <Card className="grid grid-cols-2 gap-0 overflow-hidden md:grid-cols-4">
        <InfoCell label="Version" value={d.server.version} />
        <InfoCell label="Port" value={String(d.server.port)} />
        <InfoCell label="Node env" value={d.server.nodeEnv} />
        <InfoCell label="Require auth" value={d.server.requireAuth ? 'yes' : 'no'} />
      </Card>

      <SectionLabel>Authentik</SectionLabel>
      <Card>
        <Kv label="Base URL" value={d.authentik.baseUrl} />
        <Kv label="Client ID" value={d.authentik.clientId} />
        <Kv label="Client secret" value={d.authentik.clientSecretSet ? 'set' : 'not set'} />
        <Kv label="Redirect URI" value={d.authentik.redirectUri} />
      </Card>

      <SectionLabel>Connectors</SectionLabel>
      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-[11px] tracking-wide text-[var(--color-ink-faint)] uppercase">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">MCP URL</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Auth</th>
              <th className="px-3 py-2 text-right">Tools</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.name} className="border-t border-[var(--color-line)]">
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="max-w-[220px] truncate px-3 py-2 font-mono text-[11px] text-[var(--color-ink-faint)]" title={'url' in c ? c.url : ''}>
                  {'url' in c && c.url ? c.url : '—'}
                </td>
                <td className="px-3 py-2">
                  <ConnectorStatus c={c} />
                </td>
                <td className="px-3 py-2 text-xs text-[var(--color-ink-muted)]">
                  {'authType' in c ? c.authType : ''}
                </td>
                <td className="px-3 py-2 text-right text-xs font-semibold">
                  <ToolCountCell counts={toolCounts.data} name={c.name} configured={isConnectorConfigured(c)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {d.isAdmin ? (
        <AdminPanels />
      ) : (
        <Card className="mt-4 p-4 text-sm text-[var(--color-ink-muted)]">
          <strong className="text-[var(--color-ink)]">Bot admin</strong> controls are only visible to
          addresses in <code className="text-[10px]">LOOM_BOT_ADMIN_EMAILS</code> (Coolify).
        </Card>
      )}
    </div>
  );
}

function isConnectorConfigured(c: ConnectorRow): boolean {
  if ('userConnected' in c) {
    return !!(c.userConnected || c.userApiKeySet || c.shared);
  }
  return !!('configured' in c && c.configured);
}

function ConnectorStatus({ c }: { c: ConnectorRow }) {
  if ('userEnabled' in c && c.userEnabled === false) {
    return <Badge>Hidden</Badge>;
  }
  const connected =
    'userConnected' in c
      ? !!(c.userConnected || c.userApiKeySet || c.shared)
      : !!('connected' in c && c.connected);
  if (connected) return <Badge tone="good">Connected</Badge>;
  if ('userApiKeySet' in c && c.userApiKeySet) return <Badge tone="good">Key set</Badge>;
  if ('authType' in c && (c.authType === 'oauth' || c.authType === 'external-oauth')) {
    return <Badge tone="warn">Not connected</Badge>;
  }
  if ('supportsUserApiKey' in c && c.supportsUserApiKey) {
    return <Badge tone="warn">Need API key</Badge>;
  }
  return <Badge tone="warn">Offline</Badge>;
}

function ToolCountCell({
  counts,
  name,
  configured,
}: {
  counts: ToolCounts | undefined;
  name: string;
  configured: boolean;
}) {
  if (!configured) return <span className="text-[var(--color-ink-faint)]">—</span>;
  const entry = counts?.[name];
  if (entry && typeof entry === 'object' && 'count' in entry) {
    return <span>{entry.count}</span>;
  }
  if (counts?._failed?.includes(name)) {
    return (
      <span className="text-[var(--color-warn)]" title="Could not probe tool count">
        ?
      </span>
    );
  }
  if (!counts) return <span className="text-[var(--color-ink-faint)]">…</span>;
  return <span className="text-[var(--color-ink-faint)]">—</span>;
}

function InfoCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-r border-b border-[var(--color-line)] p-4 last:border-r-0">
      <div className="text-[11px] tracking-wide text-[var(--color-ink-faint)] uppercase">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold">{value}</div>
    </div>
  );
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-4 border-b border-[var(--color-line)] px-4 py-3 last:border-b-0">
      <div className="w-28 shrink-0 text-[11px] tracking-wide text-[var(--color-ink-faint)] uppercase">{label}</div>
      <div className="min-w-0 flex-1 truncate text-sm font-medium">{value}</div>
    </div>
  );
}

function AdminPanels() {
  return (
    <>
      <AllBotsMasterSwitch />
      <PersonalBotsAdmin />
      <AdminsPanel />
    </>
  );
}

type BotRuntimeState = {
  botsEnabled?: boolean;
  updatedAt?: string;
  updatedBy?: string;
  reason?: string;
  runtime?: string;
  live?: { total: number; running: number; stopped: number };
  lastFanOut?: { ok: number; failed: number; errors: string[] } | null;
};

function AllBotsMasterSwitch() {
  const queryClient = useQueryClient();
  const runtime = useQuery({
    queryKey: ['bot-runtime'],
    queryFn: () => api.get<BotRuntimeState>('/api/admin/bot-runtime'),
    refetchInterval: 5000,
  });
  const [pending, setPending] = useState<'stop' | 'resume' | null>(null);
  const [busy, setBusy] = useState(false);

  const enabled = runtime.data?.botsEnabled !== false;
  const live = runtime.data?.live;
  const lastChange = runtime.data?.updatedAt
    ? [
        new Date(runtime.data.updatedAt).toLocaleString(),
        runtime.data.updatedBy,
        runtime.data.reason,
      ]
        .filter(Boolean)
        .join(' · ')
    : null;

  async function apply(next: boolean) {
    setBusy(true);
    try {
      await api.patch<BotRuntimeState>('/api/admin/bot-runtime', {
        botsEnabled: next,
        reason: next ? 'admin resume' : 'admin stop',
      });
      toast.success(next ? 'All bots resumed on Loomy' : 'All bots stopped on Loomy');
      await queryClient.invalidateQueries({ queryKey: ['bot-runtime'] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update bot runtime');
    } finally {
      setBusy(false);
      setPending(null);
    }
  }

  return (
    <>
      <SectionLabel>All bots</SectionLabel>
      <Card className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">Master switch</span>
              {runtime.isLoading ? (
                <Badge tone="warn">Checking…</Badge>
              ) : runtime.isError ? (
                <Badge tone="bad">Unavailable</Badge>
              ) : (
                <Badge tone={enabled ? 'good' : 'bad'}>
                  {enabled ? 'Responding' : 'Stopped'}
                </Badge>
              )}
            </div>
            <p className="text-sm text-[var(--color-ink-muted)]">
              One click stops every bot on the Loomy runtime (@loomy and personal bots): each bot
              gets <code className="text-[10px]">/stop</code> (WebSocket closed, row kept). Resume
              only restarts bots this switch previously stopped.
            </p>
            {runtime.isError ? (
              <p className="mt-2 text-xs text-[var(--color-danger)]">
                {runtime.error instanceof Error
                  ? runtime.error.message
                  : 'Could not reach the bot runtime'}
                . Needs{' '}
                <code className="text-[10px]">LOOMY_API_URL</code> (preferred) or{' '}
                <code className="text-[10px]">BOT_SERVER_URL</code> on Loom Coolify.
              </p>
            ) : (
              <>
                {live ? (
                  <p className="mt-2 text-[11px] text-[var(--color-ink-faint)]">
                    Loomy live: {live.running} running · {live.stopped} stopped · {live.total} total
                  </p>
                ) : null}
                {lastChange ? (
                  <p className="mt-1 text-[11px] text-[var(--color-ink-faint)]">Last change: {lastChange}</p>
                ) : null}
                {runtime.data?.lastFanOut && runtime.data.lastFanOut.failed > 0 ? (
                  <p className="mt-1 text-[11px] text-[var(--color-danger)]">
                    Last fan-out: {runtime.data.lastFanOut.ok} ok, {runtime.data.lastFanOut.failed}{' '}
                    failed
                    {runtime.data.lastFanOut.errors[0]
                      ? ` — ${runtime.data.lastFanOut.errors[0]}`
                      : ''}
                  </p>
                ) : null}
              </>
            )}
          </div>
          <Button
            size="sm"
            variant={enabled ? 'danger' : 'primary'}
            disabled={busy || runtime.isLoading || runtime.isError}
            onClick={() => setPending(enabled ? 'stop' : 'resume')}
            style={
              enabled
                ? { background: 'var(--color-danger)', color: '#ffffff' }
                : undefined
            }
          >
            {enabled ? 'Stop all bots' : 'Resume all bots'}
          </Button>
        </div>
      </Card>

      {pending === 'stop' ? (
        <ConfirmDialog
          title="Stop all bots?"
          body="Every running bot on Loomy will be stopped (WebSocket closed). Rows stay so you can resume."
          warn="This fans out per-bot stop — not a soft mute. Resume only restarts bots stopped by this switch."
          confirmText="Stop all"
          onClose={() => setPending(null)}
          onConfirm={() => void apply(false)}
        />
      ) : null}
      {pending === 'resume' ? (
        <ConfirmDialog
          title="Resume all bots?"
          body="Bots previously stopped by this Master switch will be started again on Loomy."
          confirmText="Resume"
          danger={false}
          onClose={() => setPending(null)}
          onConfirm={() => void apply(true)}
        />
      ) : null}
    </>
  );
}

function PersonalBotsAdmin() {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ['admin-personal-bots'],
    queryFn: () =>
      api.get<{ bots: { userId: string; botUsername?: string; ownerEmail?: string }[] }>(
        '/api/admin/personal-bots',
      ),
  });
  const [confirmId, setConfirmId] = useState<string | null>(null);

  return (
    <>
      <SectionLabel>Personal bots</SectionLabel>
      <Card className="p-4">
        <ul className="space-y-1 text-sm">
          {(list.data?.bots ?? []).map((b) => (
            <li key={b.userId} className="flex items-center justify-between">
              <span>
                @{b.botUsername ?? b.userId} {b.ownerEmail ? `· ${b.ownerEmail}` : ''}
              </span>
              <Button variant="danger" size="sm" onClick={() => setConfirmId(b.userId)}>
                Deactivate
              </Button>
            </li>
          ))}
        </ul>
      </Card>
      {confirmId ? (
        <ConfirmDialog
          title="Deactivate this personal bot?"
          body="The owner will need to recreate it."
          confirmText="Deactivate"
          onClose={() => setConfirmId(null)}
          onConfirm={() => {
            const id = confirmId;
            setConfirmId(null);
            void api.delete(`/api/admin/personal-bots/${encodeURIComponent(id)}`).then(() => {
              toast.success('Deactivated');
              void queryClient.invalidateQueries({ queryKey: ['admin-personal-bots'] });
            });
          }}
        />
      ) : null}
    </>
  );
}

function AdminsPanel() {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ['admins'],
    queryFn: () => api.get<{ admins: { email: string; source: string }[] }>('/api/admin/admins'),
  });
  const [email, setEmail] = useState('');

  return (
    <>
      <SectionLabel>Admins</SectionLabel>
      <Card className="p-4">
        <ul className="mb-3 space-y-1 text-sm">
          {(list.data?.admins ?? []).map((row) => (
            <li key={row.email} className="flex items-center justify-between">
              <span>
                {row.email}{' '}
                <span className="text-xs text-[var(--color-ink-faint)]">{row.source}</span>
              </span>
              <Button
                variant="danger"
                size="sm"
                onClick={() =>
                  void api.delete(`/api/admin/admins/${encodeURIComponent(row.email)}`).then(() => {
                    toast.success('Removed');
                    void queryClient.invalidateQueries({ queryKey: ['admins'] });
                  })
                }
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <Input placeholder="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button
            size="sm"
            onClick={() =>
              void api.post('/api/admin/admins', { email }).then(() => {
                toast.success('Added');
                setEmail('');
                void queryClient.invalidateQueries({ queryKey: ['admins'] });
              })
            }
          >
            Add
          </Button>
        </div>
      </Card>
    </>
  );
}
